/**
 * ArtifactFrame — type-agnostic shell for any Workspace artifact.
 *
 * Renders the envelope (title, subtitle, affordances, citations, version
 * chain, trace link). Per-type renderers plug into the body via `children`.
 *
 * Contract: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import type { ReactNode } from "react"
import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Artifact, ArtifactAction, ArtifactType, Citation } from "@/api/models"
import type { LucideIcon } from "@/components/icons"

import {
  CheckSquare,
  ChevronDown,
  Clock,
  FileSpreadsheet,
  FileText,
  FileType,
  HelpCircle,
  History,
  Image as ImageIcon,
  ListTodo,
  Mail,
  MoreHorizontal,
  Presentation,
  Send,
  Sparkles,
  Table as TableIcon,
  Zap,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { CitationPeekContent } from "@/features/chat/components/citation-peek-content"
import { cn } from "@/lib/utils"

const TYPE_ICON: Record<ArtifactType, LucideIcon> = {
  document: FileText,
  short_answer: Sparkles,
  email_draft: Mail,
  table: TableIcon,
  list: ListTodo,
  docx: FileText,
  xlsx: FileSpreadsheet,
  pptx: Presentation,
  pdf: FileType,
  image: ImageIcon,
  diagram: FileType,
  action_receipt: CheckSquare,
  pending_action: Clock,
  ask_user: HelpCircle,
  plan: ListTodo,
  unknown: HelpCircle,
}

const TYPE_TINT: Partial<Record<ArtifactType, string>> = {
  // Subtle background tints — kept very light. The body should feel like the
  // doc, not the chrome, so most types fall through to the default border-only.
  email_draft: "bg-muted/50",
  pending_action: "bg-warning/30 ",
  action_receipt: "bg-success/10",
  ask_user: "bg-warning/30 ",
}

function getTypeIcon(type: ArtifactType): LucideIcon {
  // The `Record<ArtifactType, LucideIcon>` is exhaustive at the type level,
  // but defend against unknown future ArtifactType values that may slip past
  // at runtime (e.g. backend ahead of the frontend type narrowing). Mirrors
  // the pattern used by `ArtifactChip` in WorkspaceLayout.tsx.
  const ICON_LOOKUP = TYPE_ICON as Record<string, LucideIcon | undefined>
  return ICON_LOOKUP[type] ?? HelpCircle
}

export interface ArtifactFrameProps {
  artifact: Artifact
  /** Full lineage for the version selector (oldest → newest). */
  versionChain?: Artifact[]
  /** Generic action dispatcher. Special-cased ids: `view_trace`, `show_history`. */
  onAction?: (actionId: string, artifact: Artifact) => void
  /** User picked a different version from the dropdown. Receives the selected version's id. */
  onSelectVersion?: (artifactId: string) => void
  /** Called when the user clicks the trace link or the `view_trace` action. */
  onViewTrace?: (missionId: string) => void
  /** Called when the user picks the `show_history` action. */
  onShowHistory?: () => void
  /** Body content — owned by the per-type renderer. */
  children: ReactNode
  className?: string
}

interface PendingConfirm {
  action: ArtifactAction
}

function ActionButton({
  action,
  onClick,
}: {
  action: ArtifactAction
  onClick: (action: ArtifactAction) => void
}) {
  // `disabled` isn't on the TS interface yet (W1 is adding it in parallel);
  // read it via a narrow cast so backend-gated actions render as disabled
  // and short-circuit the click. Once `ArtifactAction.disabled` lands this
  // cast can be cleaned up.
  const disabled = (action as ArtifactAction & { disabled?: boolean }).disabled === true
  return (
    <Button
      data-testid={`artifact-action-${action.id}`}
      disabled={disabled}
      size="sm"
      variant={action.primary ? "default" : "outline"}
      onClick={() => {
        if (disabled) return
        onClick(action)
      }}
    >
      {action.icon === "send" ? <Send aria-hidden className="size-4" /> : null}
      <span>{action.label}</span>
    </Button>
  )
}

/**
 * TODO(workspace-citations): swap this lightweight HoverCard peek for the
 * full `CitationInlinePeek` once the workspace has the chat-side plumbing
 * the inline peek requires (citation-string encoder, `findChunkForUrlCitation`,
 * `citationSection`, and a workspace document viewer to receive
 * `onViewDocument`). The contract `Citation` shape is `{ id, source_id, page?,
 * quote? }`; the chat peek expects an encoded citation string parsed by
 * `parseCitationParts`.
 */
function CitationPill({ citation }: { citation: Citation }) {
  const label = `${citation.source_id}${citation.page != null ? `:${citation.page}` : ""}`
  // Plain pill (no extra context to peek at) — keep the title fallback so
  // hovering still surfaces the quote in the native tooltip.
  if (!citation.quote && citation.page == null) {
    return (
      <span
        className="bg-background/60 rounded-sm border px-1.5 py-0.5 font-mono text-[11px]"
        data-citation-id={citation.id}
        data-testid="artifact-citation-pill"
      >
        {label}
      </span>
    )
  }

  return (
    <HoverCard closeDelay={150} openDelay={200}>
      <HoverCardTrigger asChild>
        <button
          className="bg-background/60 hover:bg-background focus-visible:ring-ring cursor-default rounded-sm border px-1.5 py-0.5 font-mono text-[11px] transition-colors focus:outline-none focus-visible:ring-2"
          data-citation-id={citation.id}
          data-testid="artifact-citation-pill"
          type="button"
        >
          {label}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80" data-testid="artifact-citation-peek">
        <CitationPeekContent
          interactionMode="static"
          pageNumber={citation.page ?? null}
          quoteText={citation.quote ?? null}
          title={citation.source_id}
          // No workspace doc viewer wired up yet; "View in document" lives in
          // the artifact's own actions, not in the citation peek.
          onViewDocument={() => {
            /* no-op until workspace doc viewer exists; see TODO above */
          }}
        />
      </HoverCardContent>
    </HoverCard>
  )
}

function CitationsRow({ citations }: { citations: Citation[] }) {
  const { t } = useTranslation()
  if (citations.length === 0) return null
  return (
    <div
      className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs"
      data-testid="artifact-citations"
    >
      <span className="font-medium">
        {t("deepExplorer.artifact_frame.citations", {
          count: citations.length,
          // react-i18next's not-ready fallback returns defaultValue without
          // interpolation, so keep the fallback fully rendered.
          defaultValue:
            citations.length === 1
              ? `Citations · ${citations.length} source`
              : `Citations · ${citations.length} sources`,
        })}
      </span>
      <span aria-hidden className="opacity-50">
        ·
      </span>
      <ul className="flex flex-wrap items-center gap-1">
        {citations.map((c) => (
          <li key={c.id}>
            <CitationPill citation={c} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function VersionSelector({
  current,
  chain,
  onSelect,
}: {
  current: Artifact
  chain: Artifact[]
  onSelect?: (artifactId: string) => void
}) {
  const { t } = useTranslation()
  // Newest first in the dropdown — version field is monotonically increasing.
  const sorted = useMemo(() => [...chain].sort((a, b) => b.version - a.version), [chain])
  const latest = sorted[0]
  const currentLabel =
    current.version === latest.version
      ? t("deepExplorer.artifact_frame.current", { defaultValue: "current" })
      : null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-7 px-2 text-xs"
          data-testid="artifact-version-trigger"
          size="sm"
          variant="ghost"
        >
          v{current.version}
          {currentLabel ? <span className="ml-1 opacity-70">({currentLabel})</span> : null}
          <ChevronDown aria-hidden className="ml-1 size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {sorted.map((a) => {
          const isLatest = a.version === latest.version
          return (
            <DropdownMenuItem
              key={a.id}
              data-testid={`artifact-version-item-${a.version}`}
              onClick={() => onSelect?.(a.id)}
            >
              v{a.version}
              {isLatest ? (
                <span className="text-muted-foreground ml-1">
                  (
                  {t("deepExplorer.artifact_frame.current", {
                    defaultValue: "current",
                  })}
                  )
                </span>
              ) : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function ArtifactFrame({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
  children,
  className,
}: ArtifactFrameProps) {
  const { t } = useTranslation()
  const [pending, setPending] = useState<PendingConfirm | null>(null)

  const Icon = getTypeIcon(artifact.type)
  const tint = TYPE_TINT[artifact.type]
  const citations = artifact.citations ?? []
  const allActions = artifact.actions ?? []
  const hasMultipleVersions = (versionChain?.length ?? 0) > 1
  const missionId = artifact.trace_ref.mission_id || null

  // Filter `show_history` out if there's no chain. Filter `view_trace` so it
  // never appears as a plain affordance — it's rendered as the trace link.
  const surfacedActions = allActions.filter((a) => {
    if (a.id === "show_history" && !hasMultipleVersions) return false
    if (a.id === "view_trace") return false
    return true
  })

  // Up to 2 primary actions in the title bar; everything else collapses to ⋯.
  const primaryActions: ArtifactAction[] = []
  const overflowActions: ArtifactAction[] = []
  for (const a of surfacedActions) {
    if (a.primary && primaryActions.length < 2) {
      primaryActions.push(a)
    } else {
      overflowActions.push(a)
    }
  }

  function dispatch(action: ArtifactAction) {
    if (action.id === "show_history") {
      onShowHistory?.()
      return
    }
    if (action.id === "view_trace") {
      if (missionId) onViewTrace?.(missionId)
      return
    }
    onAction?.(action.id, artifact)
  }

  function handleActionClick(action: ArtifactAction) {
    if (action.requires_confirmation) {
      setPending({ action })
      return
    }
    dispatch(action)
  }

  return (
    <div
      data-artifact-id={artifact.id}
      data-artifact-type={artifact.type}
      data-testid="artifact-frame"
      className={cn(
        "bg-card text-card-foreground flex flex-col overflow-hidden rounded-md border shadow-sm",
        tint,
        className
      )}
    >
      {/* Title bar */}
      <header className="flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <Icon
            aria-hidden
            className="text-muted-foreground mt-0.5 size-5 shrink-0"
            data-icon-type={artifact.type}
            data-testid="artifact-type-icon"
          />
          <div className="min-w-0">
            <h2
              className="truncate text-sm leading-snug font-semibold"
              data-testid="artifact-title"
            >
              {artifact.title}
            </h2>
            {artifact.subtitle ? (
              <p
                className="text-muted-foreground mt-0.5 truncate text-xs"
                data-testid="artifact-subtitle"
              >
                {artifact.subtitle}
              </p>
            ) : null}
          </div>
        </div>

        {(primaryActions.length > 0 || overflowActions.length > 0) && (
          <div className="flex shrink-0 items-center gap-1.5">
            {primaryActions.map((a) => (
              <ActionButton key={a.id} action={a} onClick={handleActionClick} />
            ))}
            {overflowActions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="size-8"
                    data-testid="artifact-overflow-trigger"
                    size="icon"
                    variant="ghost"
                    aria-label={t("deepExplorer.artifact_frame.more_actions", {
                      defaultValue: "More actions",
                    })}
                  >
                    <MoreHorizontal aria-hidden className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {overflowActions.map((a) => {
                    const aDisabled =
                      (a as ArtifactAction & { disabled?: boolean }).disabled === true
                    return (
                      <DropdownMenuItem
                        key={a.id}
                        data-testid={`artifact-action-overflow-${a.id}`}
                        disabled={aDisabled}
                        onClick={() => {
                          if (aDisabled) return
                          handleActionClick(a)
                        }}
                      >
                        {a.label}
                      </DropdownMenuItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </header>

      {/* Body — per-type renderer */}
      <div className="min-h-0 flex-1 px-4 py-3" data-testid="artifact-body">
        {children}
      </div>

      {/* Footer — citations, version selector, trace link */}
      <footer className="bg-muted/30 flex items-center justify-between gap-3 border-t px-4 py-2">
        <div className="min-w-0 flex-1">
          <CitationsRow citations={citations} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {hasMultipleVersions && versionChain ? (
            <VersionSelector chain={versionChain} current={artifact} onSelect={onSelectVersion} />
          ) : null}
          {missionId ? (
            <Button
              className="h-7 gap-1 px-2 text-xs"
              data-testid="artifact-view-trace"
              size="sm"
              variant="ghost"
              onClick={() => onViewTrace?.(missionId)}
            >
              <Zap aria-hidden className="size-3" />
              <span>
                {t("deepExplorer.artifact_frame.view_trace", { defaultValue: "View trace" })}
              </span>
            </Button>
          ) : null}
          {hasMultipleVersions && allActions.some((a) => a.id === "show_history") ? (
            <Button
              className="h-7 gap-1 px-2 text-xs"
              data-testid="artifact-show-history"
              size="sm"
              variant="ghost"
              onClick={() => onShowHistory?.()}
            >
              <History aria-hidden className="size-3" />
              <span>{t("deepExplorer.artifact_frame.history", { defaultValue: "History" })}</span>
            </Button>
          ) : null}
        </div>
      </footer>

      {/* Confirmation dialog for `requires_confirmation: true` actions */}
      <AlertDialog open={pending != null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent data-testid="artifact-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("deepExplorer.artifact_frame.confirm_title", {
                action:
                  pending?.action.label ??
                  t("deepExplorer.artifact_frame.confirm_default_action", {
                    defaultValue: "action",
                  }),
                defaultValue: "Confirm {{action}}",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("deepExplorer.artifact_frame.confirm_description", {
                action:
                  pending?.action.label.toLowerCase() ??
                  t("deepExplorer.artifact_frame.confirm_default_continue", {
                    defaultValue: "continue",
                  }),
                defaultValue: "This action can't be undone. Are you sure you want to {{action}}?",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="artifact-confirm-cancel">
              {t("deepExplorer.artifact_frame.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="artifact-confirm-accept"
              onClick={() => {
                if (pending) {
                  const a = pending.action
                  setPending(null)
                  dispatch(a)
                }
              }}
            >
              {pending?.action.label ??
                t("deepExplorer.artifact_frame.confirm", { defaultValue: "Confirm" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
